import { useMemo, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/app/AuthProvider';
import { ROLE_LABELS, ROLES, type Role } from '@/lib/permissions';
import { formatDateOnly } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { BottomSheet, BottomSheetBody, BottomSheetHeader } from '@/components/ui/BottomSheet';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { trackedKpiUnit } from '@/features/intelligence/dashboard/catalog';
import type { KpiDefinitionLite, KpiSnapshotLite } from '@/features/intelligence/dashboard/datasets';
import {
  TRACKED_KPI_UNIT_OPTIONS,
  slugifyKpiKey,
  useCreateKpiDefinition,
  useDeleteKpiDefinition,
  useRecordKpiValue,
  type KpiDefinitionInput,
} from '@/features/intelligence/dashboard/kpiTracking';
import { displayToNatural, formatUnit, todayIso, unitInputSuffix } from '@/features/intelligence/dashboard/series';
import { useDashboard } from './DashboardContext';

interface TrackedKpisDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ASSIGNABLE_ROLES: Role[] = ROLES.filter((r) => r !== 'external_lender');

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong.';
}

function latestSnapshot(snapshots: KpiSnapshotLite[], definitionId: string): KpiSnapshotLite | null {
  let latest: KpiSnapshotLite | null = null;
  for (const s of snapshots) {
    if (s.kpi_definition_id !== definitionId) continue;
    if (!latest || s.as_of > latest.as_of) latest = s;
  }
  return latest;
}

function RecordValueForm({ def, onDone }: { def: KpiDefinitionLite; onDone: () => void }) {
  const unit = trackedKpiUnit(def.unit);
  const record = useRecordKpiValue();
  const [asOf, setAsOf] = useState(todayIso());
  const [text, setText] = useState('');
  const parsed = Number(text);
  const valid = text.trim() !== '' && Number.isFinite(parsed) && /^\d{4}-\d{2}-\d{2}$/.test(asOf);
  const suffix = unitInputSuffix(unit);

  return (
    <form
      className="mt-2 flex flex-wrap items-end gap-2 rounded-lg bg-secondary/50 p-3"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!valid) return;
        try {
          await record.mutateAsync({ definitionId: def.id, asOf, value: displayToNatural(parsed, unit) });
          toast.success(`Recorded ${def.label} for ${formatDateOnly(asOf)}.`);
          onDone();
        } catch (err) {
          toast.error(errorMessage(err));
        }
      }}
    >
      <div className="space-y-1">
        <Label htmlFor={`asof-${def.id}`} className="text-xs text-muted-foreground">
          As of
        </Label>
        <Input id={`asof-${def.id}`} type="date" value={asOf} max={todayIso()} onChange={(e) => setAsOf(e.target.value)} className="h-9 w-40" />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`value-${def.id}`} className="text-xs text-muted-foreground">
          Value{suffix ? ` (${suffix})` : ''}
        </Label>
        <Input
          id={`value-${def.id}`}
          type="number"
          step="any"
          inputMode="decimal"
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="h-9 w-36"
        />
      </div>
      <div className="flex gap-1.5">
        <Button type="submit" size="sm" disabled={!valid || record.isPending}>
          {record.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function KpiRow({
  def,
  latest,
  canRecord,
  canManage,
}: {
  def: KpiDefinitionLite;
  latest: KpiSnapshotLite | null;
  canRecord: boolean;
  canManage: boolean;
}) {
  const unit = trackedKpiUnit(def.unit);
  const [recording, setRecording] = useState(false);
  const remove = useDeleteKpiDefinition();
  const lowerIsBetter = def.direction === 'lower_is_better';

  return (
    <li className="py-3">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium text-foreground">{def.label}</span>
            <span className="inline-flex items-center gap-0.5 rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              {lowerIsBetter ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />}
              {lowerIsBetter ? 'lower is better' : 'higher is better'}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            <span className="font-mono">{def.key}</span>
            {def.target_value != null && <> · target {formatUnit(def.target_value, unit)}</>}
            {' · visible to '}
            {def.visible_to_roles.map((r) => ROLE_LABELS[r as Role] ?? r).join(', ')}
          </p>
        </div>
        <div className="text-right">
          <p className="text-lg font-medium tabular-nums text-foreground">{latest ? formatUnit(latest.value, unit) : '—'}</p>
          <p className="text-[11px] text-muted-foreground">{latest ? `as of ${formatDateOnly(latest.as_of)}` : 'no values yet'}</p>
        </div>
        {(canRecord || canManage) && (
          <div className="flex shrink-0 items-center gap-1">
            {canRecord && (
              <Button size="sm" variant="outline" onClick={() => setRecording((r) => !r)}>
                Record value
              </Button>
            )}
            {canManage && (
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-muted-foreground hover:text-fresh-salmon-dark"
                aria-label={`Delete ${def.label}`}
                disabled={remove.isPending}
                onClick={async () => {
                  if (!window.confirm(`Delete "${def.label}" and all of its recorded values?`)) return;
                  try {
                    await remove.mutateAsync(def.id);
                    toast.success(`Deleted ${def.label}.`);
                  } catch (err) {
                    toast.error(errorMessage(err));
                  }
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>
      {recording && <RecordValueForm def={def} onDone={() => setRecording(false)} />}
    </li>
  );
}

function NewKpiForm({ existingKeys, onDone }: { existingKeys: Set<string>; onDone: () => void }) {
  const create = useCreateKpiDefinition();
  const [label, setLabel] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [key, setKey] = useState('');
  const [unit, setUnit] = useState('currency');
  const [direction, setDirection] = useState<KpiDefinitionInput['direction']>('higher_is_better');
  const [targetText, setTargetText] = useState('');
  const [roles, setRoles] = useState<Role[]>(['super_admin', 'admin']);

  const effectiveKey = keyTouched ? key : slugifyKpiKey(label);
  const duplicate = existingKeys.has(effectiveKey);
  const target = targetText.trim() === '' ? null : Number(targetText);
  const valid = label.trim().length > 0 && effectiveKey.length > 0 && !duplicate && (target === null || Number.isFinite(target)) && roles.length > 0;
  const unitType = trackedKpiUnit(unit);
  const suffix = unitInputSuffix(unitType);

  return (
    <form
      className="space-y-3 rounded-lg border border-border p-4"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!valid) return;
        try {
          await create.mutateAsync({
            key: effectiveKey,
            label: label.trim(),
            unit,
            direction,
            target_value: target === null ? null : displayToNatural(target, unitType),
            visible_to_roles: roles.includes('super_admin') ? roles : ['super_admin', ...roles],
          });
          toast.success(`Added ${label.trim()}. Find it under “Tracked KPIs” when adding a metric.`);
          onDone();
        } catch (err) {
          toast.error(errorMessage(err));
        }
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="kpi-label" className="text-xs text-muted-foreground">
            Name
          </Label>
          <Input id="kpi-label" value={label} autoFocus placeholder="e.g. Net Promoter Score" onChange={(e) => setLabel(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="kpi-key" className="text-xs text-muted-foreground">
            Key
          </Label>
          <Input
            id="kpi-key"
            value={effectiveKey}
            placeholder="auto"
            className={cn('font-mono', duplicate && 'border-fresh-salmon')}
            onChange={(e) => {
              setKeyTouched(true);
              setKey(slugifyKpiKey(e.target.value));
            }}
          />
          {duplicate && <p className="text-xs text-fresh-salmon-dark">A KPI with this key already exists.</p>}
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Unit</Label>
          <Select value={unit} onValueChange={setUnit}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRACKED_KPI_UNIT_OPTIONS.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Good direction</Label>
          <Select value={direction} onValueChange={(v) => setDirection(v as KpiDefinitionInput['direction'])}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="higher_is_better">Higher is better</SelectItem>
              <SelectItem value="lower_is_better">Lower is better</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="kpi-target" className="text-xs text-muted-foreground">
            Target{suffix ? ` (${suffix})` : ''}
          </Label>
          <Input id="kpi-target" type="number" step="any" inputMode="decimal" value={targetText} placeholder="Optional" onChange={(e) => setTargetText(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Visible to</Label>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 pt-1">
            {ASSIGNABLE_ROLES.map((r) => {
              const checked = roles.includes(r);
              const locked = r === 'super_admin';
              return (
                <label key={r} className={cn('flex items-center gap-2 text-sm', locked && 'opacity-70')}>
                  <Checkbox
                    checked={locked || checked}
                    disabled={locked}
                    ariaLabel={ROLE_LABELS[r]}
                    onCheckedChange={() => setRoles((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]))}
                  />
                  {ROLE_LABELS[r]}
                </label>
              );
            })}
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={!valid || create.isPending}>
          {create.isPending ? 'Adding…' : 'Add KPI'}
        </Button>
      </div>
    </form>
  );
}

function TrackedKpisBody() {
  const { bag, pending, errors } = useDashboard();
  const { role } = useAuth();
  const [adding, setAdding] = useState(false);
  const canManage = role === 'super_admin';
  const canRecord = role === 'super_admin' || role === 'admin';
  const data = bag.kpi_tracked;
  const loading = pending.has('kpi_tracked') && !data;
  const error = errors.kpi_tracked;

  const definitions = useMemo(() => [...(data?.definitions ?? [])].sort((a, b) => a.label.localeCompare(b.label)), [data]);
  const existingKeys = useMemo(() => new Set(definitions.map((d) => d.key)), [definitions]);

  let content: ReactNode;
  if (error) {
    content = <p className="text-sm text-fresh-salmon-dark">Couldn't load tracked KPIs: {error}</p>;
  } else if (loading) {
    content = (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  } else if (definitions.length === 0) {
    content = (
      <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
        No tracked KPIs yet. {canManage ? 'Add one below to start recording values by hand.' : 'A super admin can define them.'}
      </p>
    );
  } else {
    content = (
      <ul className="divide-y divide-border">
        {definitions.map((def) => (
          <KpiRow key={def.id} def={def} latest={latestSnapshot(data?.snapshots ?? [], def.id)} canRecord={canRecord} canManage={canManage} />
        ))}
      </ul>
    );
  }

  return (
    <div className="space-y-4">
      {content}
      {canManage &&
        (adding ? (
          <NewKpiForm existingKeys={existingKeys} onDone={() => setAdding(false)} />
        ) : (
          <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" /> New tracked KPI
          </Button>
        ))}
    </div>
  );
}

const TITLE = 'Tracked KPIs';
const DESCRIPTION = 'Numbers the system cannot compute on its own — NPS, headcount, a board target. Record values by hand and chart them like any other metric.';

export function TrackedKpisDialog({ open, onOpenChange }: TrackedKpisDialogProps) {
  const { isMobile } = useDashboard();
  if (isMobile) {
    return (
      <BottomSheet open={open} onOpenChange={onOpenChange}>
        <BottomSheetHeader title={TITLE} description={DESCRIPTION} />
        <BottomSheetBody>{open && <TrackedKpisBody />}</BottomSheetBody>
      </BottomSheet>
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{TITLE}</DialogTitle>
          <DialogDescription>{DESCRIPTION}</DialogDescription>
        </DialogHeader>
        {open && <TrackedKpisBody />}
      </DialogContent>
    </Dialog>
  );
}
